import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import TaskQueuePanel from './components/TaskQueuePanel';
import ClientInteractionsTable from './components/ClientInteractionsTable';
import QuickActionsPanel from './components/QuickActionsPanel';
import NotificationBanner from './components/NotificationBanner';
import Header from '../../components/Header';

const CaseWorkerDashboard = () => {
  const navigate = useNavigate();
  const [selectedClients, setSelectedClients] = useState([]);
  const [activePanel, setActivePanel] = useState('all');
  const [notifications, setNotifications] = useState([]);

  // Mock data for tasks
  const tasks = [
    {
      id: 1,
      title: "Erstgespräch durchführen",
      clientName: "Anna Schmidt",
      clientId: "BA-2025-10847",
      dueDate: "26.11.2025",
      dueTime: "10:00 Uhr",
      status: "pending",
      priority: "urgent",
      notes: "Neuer Klient, benötigt umfassende Beratung zu Arbeitslosengeld II und Weiterbildungsmöglichkeiten",
      tags: ["Erstgespräch", "ALG II"]
    },
    {
      id: 2,
      title: "Lebenslauf überprüfen",
      clientName: "Michael Weber",
      clientId: "BA-2025-10823",
      dueDate: "26.11.2025",
      dueTime: "14:30 Uhr",
      status: "in-progress",
      priority: "high",
      notes: "Lebenslauf muss vor Bewerbungsgespräch am 28.11. finalisiert werden",
      tags: ["Bewerbung"]
    },
    {
      id: 3,
      title: "Weiterbildungsantrag bearbeiten",
      clientName: "Sarah Müller",
      clientId: "BA-2025-10756",
      dueDate: "27.11.2025",
      dueTime: "09:00 Uhr",
      status: "pending",
      priority: "medium",
      notes: "Antrag für IT-Weiterbildung prüfen und genehmigen",
      tags: ["Weiterbildung", "IT"]
    },
    {
      id: 4,
      title: "Folgetermin vereinbaren",
      clientName: "Thomas Becker",
      clientId: "BA-2025-10691",
      dueDate: "27.11.2025",
      dueTime: "11:00 Uhr",
      status: "pending",
      priority: "low",
      notes: "Monatliches Statusgespräch zur Arbeitssuche",
      tags: ["Folgetermin"]
    },
    {
      id: 5,
      title: "Dokumentation vervollständigen",
      clientName: "Lisa Hoffmann",
      clientId: "BA-2025-10634",
      dueDate: "26.11.2025",
      dueTime: "16:00 Uhr",
      status: "pending",
      priority: "urgent",
      notes: "Fehlende Unterlagen für Leistungsantrag nachfordern",
      tags: ["Dokumentation", "Dringend"]
    },
    {
      id: 6,
      title: "Vermittlungsvorschlag erstellen",
      clientName: "Peter Klein",
      clientId: "BA-2025-10589",
      dueDate: "28.11.2025",
      dueTime: "10:30 Uhr",
      status: "pending",
      priority: "high",
      notes: "Passende Stellenangebote für Elektriker zusammenstellen",
      tags: ["Vermittlung", "Handwerk"]
    }
  ];

  // Mock data for client interactions
  const interactions = [
    {
      clientId: "BA-2025-10847",
      name: "Anna Schmidt",
      occupation: "Bürokauffrau",
      lastContact: "25.11.2025",
      contactType: "Telefonat",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Erstgespräch",
      nextActionDate: "26.11.2025",
      priority: "high"
    },
    {
      clientId: "BA-2025-10823",
      name: "Michael Weber",
      occupation: "Softwareentwickler",
      lastContact: "24.11.2025",
      contactType: "Persönlich",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Lebenslauf-Check",
      nextActionDate: "26.11.2025",
      priority: "high"
    },
    {
      clientId: "BA-2025-10756",
      name: "Sarah Müller",
      occupation: "Einzelhandelskauffrau",
      lastContact: "23.11.2025",
      contactType: "E-Mail",
      status: "pending",
      statusLabel: "Wartend",
      nextAction: "Weiterbildungsantrag",
      nextActionDate: "27.11.2025",
      priority: "medium"
    },
    {
      clientId: "BA-2025-10691",
      name: "Thomas Becker",
      occupation: "Lagerarbeiter",
      lastContact: "22.11.2025",
      contactType: "Telefonat",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Folgetermin",
      nextActionDate: "27.11.2025",
      priority: "low"
    },
    {
      clientId: "BA-2025-10634",
      name: "Lisa Hoffmann",
      occupation: "Krankenschwester",
      lastContact: "25.11.2025",
      contactType: "Persönlich",
      status: "urgent",
      statusLabel: "Dringend",
      nextAction: "Dokumentation",
      nextActionDate: "26.11.2025",
      priority: "high"
    },
    {
      clientId: "BA-2025-10589",
      name: "Peter Klein",
      occupation: "Elektriker",
      lastContact: "21.11.2025",
      contactType: "Telefonat",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Vermittlungsvorschlag",
      nextActionDate: "28.11.2025",
      priority: "high"
    },
    {
      clientId: "BA-2025-10512",
      name: "Julia Wagner",
      occupation: "Grafikdesignerin",
      lastContact: "20.11.2025",
      contactType: "E-Mail",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Portfolio-Review",
      nextActionDate: "29.11.2025",
      priority: "medium"
    },
    {
      clientId: "BA-2025-10478",
      name: "Martin Schulz",
      occupation: "KFZ-Mechaniker",
      lastContact: "19.11.2025",
      contactType: "Persönlich",
      status: "pending",
      statusLabel: "Wartend",
      nextAction: "Bewerbungstraining",
      nextActionDate: "30.11.2025",
      priority: "medium"
    },
    {
      clientId: "BA-2025-10423",
      name: "Sabine Fischer",
      occupation: "Erzieherin",
      lastContact: "18.11.2025",
      contactType: "Telefonat",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Stellensuche",
      nextActionDate: "01.12.2025",
      priority: "low"
    },
    {
      clientId: "BA-2025-10389",
      name: "Frank Richter",
      occupation: "Koch",
      lastContact: "17.11.2025",
      contactType: "E-Mail",
      status: "inactive",
      statusLabel: "Inaktiv",
      nextAction: "Statusprüfung",
      nextActionDate: "05.12.2025",
      priority: "low"
    },
    {
      clientId: "BA-2025-10345",
      name: "Claudia Braun",
      occupation: "Friseurin",
      lastContact: "25.11.2025",
      contactType: "Persönlich",
      status: "urgent",
      statusLabel: "Dringend",
      nextAction: "Notfallberatung",
      nextActionDate: "26.11.2025",
      priority: "high"
    },
    {
      clientId: "BA-2025-10298",
      name: "Andreas Zimmermann",
      occupation: "Tischler",
      lastContact: "16.11.2025",
      contactType: "Telefonat",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Werkstattbesuch",
      nextActionDate: "02.12.2025",
      priority: "medium"
    },
    {
      clientId: "BA-2025-10267",
      name: "Monika Hartmann",
      occupation: "Sekretärin",
      lastContact: "15.11.2025",
      contactType: "E-Mail",
      status: "pending",
      statusLabel: "Wartend",
      nextAction: "Antragsbearbeitung",
      nextActionDate: "03.12.2025",
      priority: "low"
    },
    {
      clientId: "BA-2025-10234",
      name: "Stefan Lang",
      occupation: "Maler",
      lastContact: "24.11.2025",
      contactType: "Persönlich",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Qualifizierung",
      nextActionDate: "28.11.2025",
      priority: "medium"
    },
    {
      clientId: "BA-2025-10189",
      name: "Petra Krause",
      occupation: "Verkäuferin",
      lastContact: "14.11.2025",
      contactType: "Telefonat",
      status: "active",
      statusLabel: "Aktiv",
      nextAction: "Bewerbungsgespräch",
      nextActionDate: "04.12.2025",
      priority: "high"
    }
  ];

  // Mock statistics
  const statistics = {
    activeClients: 127,
    todayAppointments: 8,
    pendingTasks: 23,
    urgentCases: 5
  };

  // Mock saved filters
  const savedFilters = [
    { id: 'urgent', name: 'Dringende Fälle', description: 'Klienten mit hoher Priorität', count: 5 },
    { id: 'today', name: 'Termine heute', description: 'Alle heutigen Termine', count: 8 },
    { id: 'pending-docs', name: 'Fehlende Dokumente', description: 'Unvollständige Unterlagen', count: 12 },
    { id: 'new-clients', name: 'Neue Klienten', description: 'Letzte 7 Tage', count: 15 },
    { id: 'follow-up', name: 'Folgetermine', description: 'Diese Woche', count: 18 }
  ];

  // Mock notifications
  useEffect(() => {
    setNotifications([
      {
        id: 1,
        type: 'urgent',
        title: 'Dringender Termin',
        message: 'Anna Schmidt (BA-2025-10847) wartet auf Erstgespräch um 10:00 Uhr',
        time: 'vor 5 Min.',
        action: 'Termin anzeigen'
      },
      {
        id: 2,
        type: 'system',
        title: 'Systemaktualisierung',
        message: 'Neue Version der Sozialversicherungsdatenbank verfügbar',
        time: 'vor 15 Min.',
        action: 'Details anzeigen'
      },
      {
        id: 3,
        type: 'message',
        title: 'Neue Nachricht',
        message: 'Michael Weber hat auf Ihre E-Mail geantwortet',
        time: 'vor 30 Min.',
        action: 'Nachricht lesen'
      }
    ]);
  }, []);

  const handleTaskClick = (task) => {
    console.log('Task clicked:', task);
    navigate('/client-profile-management', { state: { clientId: task?.clientId } });
  };

  const handleClientSelect = (clientIds) => {
    setSelectedClients(clientIds);
  };

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action, 'for clients:', selectedClients);
  };

  const handleSearch = (query) => {
    console.log('Search query:', query);
    navigate('/client-search-and-directory', { state: { query } });
  };

  const handleFilterApply = (filterId) => {
    console.log('Filter applied:', filterId);
  };

  const handleDismissNotification = (id) => {
    setNotifications(notifications?.filter(n => n?.id !== id));
  };

  const handleViewAllNotifications = () => {
    console.log('View all notifications');
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.ctrlKey) {
        switch (e?.key) {
          case '1':
            e?.preventDefault();
            setActivePanel('tasks');
            break;
          case '2':
            e?.preventDefault();
            setActivePanel('interactions');
            break;
          case '3':
            e?.preventDefault();
            setActivePanel('quick');
            break;
          case 'f':
            e?.preventDefault();
            document.querySelector('input[type="search"]')?.focus();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50">
        {/* Notifications */}
        <NotificationBanner
          notifications={notifications}
          onDismiss={handleDismissNotification}
          onViewAll={handleViewAllNotifications}
        />

        {/* Three Panel Layout */}
        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-240px)]">
          {/* Left Panel - Task Queue (25%) */}
          <div className="col-span-3 bg-card rounded-lg border border-border overflow-hidden">
            <TaskQueuePanel tasks={tasks} onTaskClick={handleTaskClick} />
          </div>

          {/* Center Panel - Client Interactions (50%) */}
          <div className="col-span-6 bg-card rounded-lg border border-border overflow-hidden">
            <ClientInteractionsTable
              interactions={interactions}
              onClientSelect={handleClientSelect}
              selectedClients={selectedClients}
              onBulkAction={handleBulkAction}
            />
          </div>

          {/* Right Panel - Quick Actions (25%) */}
          <div className="col-span-3 bg-card rounded-lg border border-border overflow-hidden">
            <QuickActionsPanel
              statistics={statistics}
              savedFilters={savedFilters}
              onSearch={handleSearch}
              onFilterApply={handleFilterApply}
            />
          </div>
        </div>

        {/* Quick Navigation Footer */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/client-search-and-directory')}
            className="p-4 bg-card rounded-lg border border-border hover:bg-muted transition-colors text-left"
          >
            <Icon name="Search" size={20} className="text-primary mb-2" />
            <div className="font-semibold text-sm">Klientensuche</div>
            <div className="text-xs text-muted-foreground">Erweiterte Suche</div>
          </button>
          <button
            onClick={() => navigate('/bulk-operations-center')}
            className="p-4 bg-card rounded-lg border border-border hover:bg-muted transition-colors text-left"
          >
            <Icon name="Layers" size={20} className="text-accent mb-2" />
            <div className="font-semibold text-sm">Massenoperationen</div>
            <div className="text-xs text-muted-foreground">Mehrere Klienten</div>
          </button>
          <button
            onClick={() => navigate('/supervisor-dashboard-and-analytics')}
            className="p-4 bg-card rounded-lg border border-border hover:bg-muted transition-colors text-left"
          >
            <Icon name="BarChart3" size={20} className="text-warning mb-2" />
            <div className="font-semibold text-sm">Berichte</div>
            <div className="text-xs text-muted-foreground">Analysen & Statistiken</div>
          </button>
          <button
            onClick={() => navigate('/audit-trail-and-compliance-viewer')}
            className="p-4 bg-card rounded-lg border border-border hover:bg-muted transition-colors text-left"
          >
            <Icon name="Shield" size={20} className="text-error mb-2" />
            <div className="font-semibold text-sm">Compliance</div>
            <div className="text-xs text-muted-foreground">Prüfpfad & Audit</div>
          </button>
        </div>
      </div>
    </>
  );
};

export default CaseWorkerDashboard;